import { useState } from "react";
import { Eye, EyeOff, Loader2, ShieldHalf, Flame, Fingerprint } from "lucide-react";
import { useVault } from "@/context/VaultContext";
import { InstallBanner } from "@/components/InstallBanner";
import { Switch } from "@/components/ui/switch";
import { LoginForm } from "@/components/aph/LoginForm";

const Field = ({ label, testId, type = "text", value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  const isPass = type === "password";
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.16em] text-ash/50 font-branding">{label}</span>
      <div className="mt-1.5 flex items-center bg-nebula border border-rim rounded-secure focus-within:border-stellar/50 transition-colors">
        <input data-testid={testId} type={isPass && !show ? "password" : "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete="off"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm text-ash placeholder-ash/30 focus:outline-none" />
        {isPass && (
          <button type="button" onClick={() => setShow((s) => !s)} className="px-3 text-ash/40 hover:text-stellar" data-testid={`${testId}-toggle`}>
            {show ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
          </button>
        )}
      </div>
    </label>
  );
};

const POINTS = [
  [Fingerprint, "Keys, not accounts", "An Ed25519 identity is forged on this device. No email. No phone number."],
  [Flame, "Burn-on-read", "Messages self-destruct on both devices after they're seen."],
  [ShieldHalf, "Decoy Vault", "A second passphrase opens a separate, believable vault."],
];

function Hero() {
  return (
    <div className="hidden lg:flex flex-col justify-between p-14 border-r border-glass relative overflow-hidden">
      <div className="absolute -right-40 top-24 w-[560px] h-[560px] rounded-full border border-stellar/10" />
      <div className="absolute -right-20 top-44 w-[380px] h-[380px] rounded-full border border-stellar/5" />
      <div className="flex items-center gap-3 relative"><img src="/icon-192.png" alt="Aphelion" className="w-10 h-10 rounded-secure border border-glass" /><span className="font-branding font-bold tracking-[0.3em] text-lg">APHELION</span></div>
      <div className="relative max-w-lg">
        <h1 className="font-branding text-5xl lg:text-6xl font-bold leading-[1.02] mb-6">Messages at the far edge of reach.</h1>
        <div className="space-y-5">
          {POINTS.map(([Icon, t, d]) => (
            <div key={t} className="flex gap-4"><Icon className="text-stellar mt-0.5 flex-shrink-0" size={20} strokeWidth={1.5} /><div><p className="font-medium">{t}</p><p className="text-sm text-ash/50">{d}</p></div></div>
          ))}
        </div>
      </div>
      <p className="relative font-mono text-[11px] text-ash/30">ED25519 · X25519 · XSALSA20-POLY1305 · AES-256-GCM · SQLITE</p>
    </div>
  );
}

export default function OnboardingPage() {
  const { create } = useVault();
  const [f, setF] = useState({ alias: "", pass: "", confirm: "", decoy: false, dAlias: "", dPass: "" });
  const [mode, setMode] = useState("create");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.alias.trim()) return setError("Choose an alias");
    if (f.pass.length < 6) return setError("Passphrase or PIN must be at least 6 characters");
    if (f.pass !== f.confirm) return setError("Passphrases don't match");
    if (f.decoy && (!f.dAlias.trim() || f.dPass.length < 6)) return setError("Decoy vault needs an alias and a 6+ character passphrase");
    if (f.decoy && f.dPass === f.pass) return setError("Decoy passphrase must differ from your real one");
    setBusy(true);
    try {
      await create({ alias: f.alias.trim(), pass: f.pass, decoy: f.decoy ? { alias: f.dAlias.trim(), pass: f.dPass } : null });
    } catch (err) {
      setError(err.message || "Could not create vault");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-void starfield text-ash grid lg:grid-cols-2" data-testid="onboarding-page">
      <Hero />
      <div className="flex items-center justify-center px-6 pt-12 pb-28 lg:py-12">
        <div className="w-full max-w-sm space-y-5 rise-in">
          <div className="lg:hidden flex items-center gap-3 mb-2"><img src="/icon-192.png" alt="Aphelion" className="w-9 h-9 rounded-secure border border-glass" /><span className="font-branding font-bold tracking-[0.3em]">APHELION</span></div>
          <div className="grid grid-cols-2 bg-nebula border border-rim rounded-secure p-1" data-testid="onboard-mode-tabs">
            {[["create", "Create identity"], ["login", "Log in"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setMode(k)} data-testid={`onboard-mode-${k}`} data-state={mode === k ? "active" : "inactive"}
                className={`py-2 text-sm font-branding rounded-[4px] transition-colors ${mode === k ? "bg-stellar text-void font-semibold" : "text-ash/60 hover:text-ash"}`}>{l}</button>
            ))}
          </div>
          {mode === "login" ? (
            <>
              <div>
                <h2 className="font-branding text-2xl font-semibold">Welcome back</h2>
                <p className="text-sm text-ash/50 mt-1">Restore your identity with the login key from your other device.</p>
              </div>
              <LoginForm />
            </>
          ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <h2 className="font-branding text-2xl font-semibold">Forge your identity</h2>
            <p className="text-sm text-ash/50 mt-1">Your keys never leave this device. Your passphrase encrypts the local vault — it cannot be recovered.</p>
          </div>
          <Field label="Alias" testId="onboard-alias-input" value={f.alias} onChange={set("alias")} placeholder="Orion" />
          <Field label="Passphrase or PIN" testId="onboard-pass-input" type="password" value={f.pass} onChange={set("pass")} placeholder="Min 6 characters" />
          <Field label="Confirm" testId="onboard-pass-confirm-input" type="password" value={f.confirm} onChange={set("confirm")} placeholder="Repeat passphrase" />
          <div className="bg-nebula border border-rim rounded-secure p-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div><p className="text-sm font-medium flex items-center gap-2"><ShieldHalf size={16} strokeWidth={1.5} className="text-stellar" /> Decoy Vault</p><p className="text-xs text-ash/45 mt-0.5">A second passphrase that opens a separate identity.</p></div>
              <Switch data-testid="onboard-decoy-toggle" checked={f.decoy} onCheckedChange={set("decoy")} />
            </div>
            {f.decoy && (
              <div className="space-y-3 rise-in">
                <Field label="Decoy alias" testId="onboard-decoy-alias-input" value={f.dAlias} onChange={set("dAlias")} placeholder="Tethys" />
                <Field label="Decoy passphrase" testId="onboard-decoy-pass-input" type="password" value={f.dPass} onChange={set("dPass")} placeholder="Different from your real one" />
              </div>
            )}
          </div>
          {error && <p className="text-sm text-flare" data-testid="onboard-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="onboard-submit-btn" className="w-full flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-3 hover:opacity-85 transition-opacity disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Forging keys…</> : "Create vault"}
          </button>
        </form>
          )}
          <a href="/privacy" className="block text-center text-xs text-ash/35 hover:text-ash/70" data-testid="onboard-privacy-link">Privacy Policy</a>
        </div>
      </div>
      <InstallBanner />
    </div>
  );
}

export { Field };
